import { selectors } from "../../../../add-element/cypressSelectors";
import API from "../../../../core/api";
import { getGuestAuthSettings } from "../../../../core/utils";
import {
  ATTENDING,
  AWAITING_REPLY,
  NOT_ATTENDING,
} from "../../../../guests/constants";
import ConfirmationModal from "../../../../minted-components-ports/ConfirmationModal";
import EventSummaryEmail from "../../../../rsvp/website/EventSummaryEmail";
import { isEmailValid } from "../../../../shared/validations/emailValidations";
import { themeTypographyResetStyles } from "../../../../website/components/themeTypographyResetStyles";
import { isGuestNamed } from "../../../dashboard/utils";
import { scrollToElement } from "../../utils";
import EventDetails from "../EventDetails";
import {
  QUESTION_WITH_ERROR_CLASSNAME,
  RSVP_LOCAL_STORAGE_KEY,
} from "../constants";
import type { Guest, GuestResponse, RSVPContact, RSVPEvent } from "../types";
import WeddingEventGuest from "./WeddingEventGuest";
import WeddingEventsGuestsSummary from "./WeddingEventsGuestsSummary";
import { css } from "@emotion/react";
import {
  Button,
  TextLink,
  theme,
  styleUtils,
  useMediaQueryState,
} from "@minted/minted-components";
import isEmpty from "lodash/isEmpty";
import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";

type Props = {
  contact: RSVPContact;
  events: RSVPEvent[];
  guests: Guest[];
  hasExistingRsvps?: boolean;
  onCancelRsvp: () => void;
};

type UpdatedGuestName = { firstName?: string; lastName?: string };

type FormData = {
  contactId: string | null;
  emails: { isValid: boolean; value: string }[];
  eventResponsesByEventId: Record<number, Record<number, GuestResponse>>;
  updatedGuestNamesByGuestId: Record<number, UpdatedGuestName>;
};

type RSVPLocalStorageState = {
  contact: RSVPContact;
  currentEventIndex: number;
  events: RSVPEvent[];
  formData: FormData;
  guests: Guest[];
  showSummary: boolean;
};

const rsvpStyles = {
  buttonsWrapper: css`
    margin-bottom: ${theme.spacing.x5};
    display: flex;
    gap: ${theme.spacing.x4};
  `,
  buttonWrapper: css`
    display: flex;
    margin-top: ${theme.spacing.x4};
    margin-bottom: ${theme.spacing.x4};
  `,
  emailMargins: css`
    margin-left: ${theme.spacing.x5};
    margin-right: ${theme.spacing.x5};
    margin-bottom: ${theme.spacing.x5};
  `,
  errorLabel: css`
    ${theme.typeStyles.bodySmallStyles};
    color: ${theme.colors.textError};
    margin-top: 0.25rem;
  `,
  formWrapper: css`
    background-color: ${theme.colors.gray200};
    display: grid;
    gap: ${theme.spacing.x5};
    padding: ${theme.spacing.x5};
    ${theme.media.greaterThan("small")`
      padding: ${theme.spacing.x4};
    `};
  `,
  fullWidthButtonWrapper: css`
    width: 100%;
  `,
  partySizeLabel: css`
    min-width: ${styleUtils.rem(100)};
  `,
  partySizeWrapper: css`
    display: flex;
    flex-direction: row;
    align-items: center;
  `,
  rsvpForAnotherGuestWrapper: css`
    margin-right: auto;
    margin-top: ${theme.spacing.x3};
  `,
  submitButtonWrapper: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x2};
    width: 100%;
  `,
  wrapper: css`
    background-color: ${theme.colors.white};
    color: ${theme.colors.textPrimary};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x4};
    letter-spacing: normal;
    line-height: normal;
    margin-top: ${theme.spacing.x5};
    margin-left: auto;
    margin-right: auto;
    max-width: ${styleUtils.rem(660)};
    padding-left: ${theme.spacing.x4};
    padding-right: ${theme.spacing.x4};
    text-align: initial;
    width: calc(100% - ${theme.spacing.x8});
  `,
};

const getInitialFormState = (
  contact: RSVPContact,
  guests: Guest[],
  events: RSVPEvent[],
): FormData => {
  const updatedGuestNamesByGuestId: Record<number, UpdatedGuestName> = {};
  const eventResponsesByEventId: Record<
    number,
    Record<number, GuestResponse>
  > = {};
  const guestIds: number[] = [];

  for (const guest of guests) {
    guestIds.push(guest.id);
    if (!isGuestNamed(guest)) {
      // Fill out unnamed guest object
      if (guests.length > 1) {
        updatedGuestNamesByGuestId[guest.id] = {
          firstName: "",
          lastName: "",
        };
      } else {
        // Only one guest, parse contact name into guest update structure
        const lastSpaceIndex = contact.name.lastIndexOf(" ");
        let firstName = "";
        let lastName = "";

        if (lastSpaceIndex > -1) {
          firstName = contact.name.substring(0, lastSpaceIndex);
          lastName = contact.name.substring(lastSpaceIndex + 1);
        } else {
          firstName = contact.name;
        }

        updatedGuestNamesByGuestId[guest.id] = {
          firstName,
          lastName,
        };
      }
    }
  }

  for (const event of events) {
    const guestData: Record<number, GuestResponse> = {};

    for (const guestId of guestIds) {
      if (event.guests.includes(guestId)) {
        guestData[guestId] = {
          answers: {},
          responseType: AWAITING_REPLY,
        };
      }
    }
    eventResponsesByEventId[event.id] = guestData;
  }

  return {
    contactId: contact.id,
    emails: [
      {
        isValid: false,
        value: "",
      },
    ],
    eventResponsesByEventId,
    updatedGuestNamesByGuestId,
  };
};

const getGuestIdsToValidate = (
  guestsResponses: Record<number, GuestResponse> = {},
): number[] => {
  const forms = Object.entries(guestsResponses);

  const formsToValidate: number[] = [];
  const responsesTypeForValidation: string[] = [ATTENDING, AWAITING_REPLY];

  for (const [guestId, responses] of forms) {
    if (responsesTypeForValidation.includes(responses.responseType)) {
      formsToValidate.push(parseInt(guestId));
    }
  }

  return formsToValidate;
};

const DESKTOP_NAVBAR_OFFSET = 54;
const MOBILE_NAVBAR_OFFSET = 44;

const getErrorMessage = (error: any) => {
  if (error?.response?.body?.detail) {
    return error.response.body.detail;
  }

  return "Unable to submit your RSVP. Please try again.";
};

// Stored state is stale if the host has since added/removed events or
// guests, since the stored eventResponsesByEventId map won't have entries
// for ids it didn't know about.
const isStoredStateStale = (
  stored: RSVPLocalStorageState,
  events: RSVPEvent[],
  guests: Guest[],
) => {
  const storedEventIds = new Set(
    (stored.events ?? []).map((event) => event.id),
  );
  const storedGuestIds = new Set(
    (stored.guests ?? []).map((guest) => guest.id),
  );

  return (
    events.length !== storedEventIds.size ||
    guests.length !== storedGuestIds.size ||
    events.some((event) => !storedEventIds.has(event.id)) ||
    guests.some((guest) => !storedGuestIds.has(guest.id))
  );
};

// Read and validate stored contact RSVP state
export const getStoredFormState = (
  contactId: string | null,
  events: RSVPEvent[],
  guests: Guest[],
): RSVPLocalStorageState | null => {
  const stored = localStorage.getItem(RSVP_LOCAL_STORAGE_KEY);

  if (stored) {
    const parsed = JSON.parse(stored) as RSVPLocalStorageState;

    if (
      parsed?.contact?.id === contactId &&
      !isStoredStateStale(parsed, events, guests)
    ) {
      return parsed;
    }
  }
  return null;
};

type InitialData = {
  currentEventIndex: number;
  formData: FormData;
  hasAcknowledgedExistingRsvp: boolean;
  showSummary: boolean;
};

const getInitialData = (
  contact: RSVPContact,
  events: RSVPEvent[],
  guests: Guest[],
  hasExistingRsvps?: boolean,
): InitialData => {
  const stored = getStoredFormState(contact.id, events, guests);

  return {
    currentEventIndex: Math.min(
      stored?.currentEventIndex ?? 0,
      events.length - 1,
    ),
    formData: stored?.formData ?? getInitialFormState(contact, guests, events),
    hasAcknowledgedExistingRsvp: !hasExistingRsvps || Boolean(stored),
    showSummary: stored?.showSummary ?? false,
  };
};

const WeddingRSVPForm = ({
  contact,
  events,
  guests,
  hasExistingRsvps,
  onCancelRsvp,
}: Props) => {
  const [initialData] = useState(() =>
    getInitialData(contact, events, guests, hasExistingRsvps),
  );
  const [formData, setFormData] = useState(initialData.formData);
  const [showSummary, setShowSummary] = useState(initialData.showSummary);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentEventIndex, setCurrentEventIndex] = useState(
    initialData.currentEventIndex,
  );

  const [hasNextBeenClicked, setHasNextBeenClicked] = useState(false);

  const [guestsFormsToValidate, setGuestsFormsToValidate] = useState<number[]>(
    [],
  );

  const [rsvpSubmitError, setRsvpSubmitError] = useState<any>(null);
  const [hasAcknowledgedExistingRsvp, setHasAcknowledgedExistingRsvp] =
    useState(initialData.hasAcknowledgedExistingRsvp);

  const wrapper = useRef<HTMLDivElement>(null);
  const mediumMediaQueryState = useMediaQueryState({
    mediaQuerySize: "medium",
  });
  const isMobile = mediumMediaQueryState === "BELOW";
  const totalEvents = events.length;
  const showSubmitButton = currentEventIndex === totalEvents - 1;
  const showNextButton = totalEvents > 1 && currentEventIndex < totalEvents - 1;
  const showBackButton = totalEvents > 1 && currentEventIndex > 0;
  const currentEvent = events[currentEventIndex];
  // used to automatic scroll on errors or next/submit click
  const navbarOffset = isMobile ? MOBILE_NAVBAR_OFFSET : DESKTOP_NAVBAR_OFFSET;

  const canEditName = useCallback(
    (guestId: number) => {
      /* Guest names can only be edited under specific circumstances. The guest must be
       * unnamed, and the current event must be the first event that the guest has not
       * yet responded "no" to. So if this is the first time the guest may attend an event,
       * we display the name prompt.
       * */
      const guest = guests.find((guest) => `${guest.id}` === `${guestId}`);

      // Can only edit unnamed guests
      if (!guest || isGuestNamed(guest)) {
        return false;
      }

      // Find first event that the guest may be attending, check if it's this one
      for (const event of events) {
        const eventResponses = formData.eventResponsesByEventId[event.id];

        // Check events in order to see if the guest is attending any before the current event
        if (event.guests.includes(guestId) && eventResponses) {
          const eventResponse = eventResponses[guest.id];

          // Return false if not attending the current event, or if attending a previous event
          if (event.id === currentEvent.id) {
            if (eventResponse?.responseType === NOT_ATTENDING) {
              return false;
            }
          } else {
            if (eventResponse?.responseType === ATTENDING) {
              return false;
            }
          }
        }
        // Stop checking once we get to the current event
        if (event.id === currentEvent.id) {
          break;
        }
      }

      return true;
    },
    [currentEvent, events, formData.eventResponsesByEventId, guests],
  );

  const isFormComplete = useMemo(() => {
    const requiredQuestions = currentEvent.questions.filter(
      (question) => question.required,
    );
    const guestResponses = formData.eventResponsesByEventId[currentEvent.id];

    for (const [guestId, response] of Object.entries(guestResponses)) {
      // Every guest must reply
      if (!response || response.responseType === AWAITING_REPLY) {
        return false;
      } else if (response.responseType === ATTENDING) {
        // Editable names must be filled in
        if (canEditName(Number(guestId))) {
          const newGuestName =
            formData.updatedGuestNamesByGuestId[Number(guestId)];

          if (!isGuestNamed(newGuestName)) {
            return false;
          }
        }
        // All required questions must be answered
        for (const question of requiredQuestions) {
          if (!response.answers[question.id]) {
            return false;
          }
        }
      }
    }

    return true;
  }, [
    canEditName,
    currentEvent.id,
    currentEvent.questions,
    formData.eventResponsesByEventId,
    formData.updatedGuestNamesByGuestId,
  ]);

  const updateGuestResponses = useCallback(
    (
      guestId: number,
      newResponses: GuestResponse,
      shouldResetFormValidation: boolean,
    ) => {
      setRsvpSubmitError(null);
      setFormData((prevFormData) => ({
        ...prevFormData,
        eventResponsesByEventId: {
          ...prevFormData.eventResponsesByEventId,
          [currentEvent.id]: {
            ...prevFormData.eventResponsesByEventId[currentEvent.id],
            // Recreate object, replacing guest responses for current event
            [guestId]: newResponses,
          },
        },
      }));

      if (shouldResetFormValidation) {
        setGuestsFormsToValidate((prevForms) =>
          prevForms.filter((form) => guestId !== form),
        );
      }
    },
    [currentEvent],
  );

  const updateGuestName = useCallback(
    (guestId: number, namePart: "firstName" | "lastName", value: string) => {
      setRsvpSubmitError(null);
      setFormData((prevState) => {
        // Get existing guest name value
        const guestName = prevState.updatedGuestNamesByGuestId[guestId] || {};

        // Update based on name type
        if (namePart === "firstName") {
          guestName.firstName = value;
        } else {
          guestName.lastName = value;
        }

        // Update form state with new value
        return {
          ...prevState,
          updatedGuestNamesByGuestId: {
            ...prevState.updatedGuestNamesByGuestId,
            [guestId]: {
              ...guestName,
            },
          },
        };
      });
    },
    [],
  );

  const updateEmails = useCallback(
    (newEmails: { isValid: boolean; value: string }[]) => {
      setRsvpSubmitError(null);
      setFormData((prevState) => ({
        ...prevState,
        emails: newEmails,
      }));
    },
    [],
  );

  const updateGuestIdsToValidate = useCallback(() => {
    const guestsFormsToValidate = getGuestIdsToValidate(
      formData.eventResponsesByEventId[currentEvent.id],
    );

    setGuestsFormsToValidate(guestsFormsToValidate);
    setHasNextBeenClicked(true);

    setTimeout(() => {
      const wrappersWithErrors = document.querySelectorAll(
        `.${QUESTION_WITH_ERROR_CLASSNAME}`,
      );

      if (!isEmpty(wrappersWithErrors)) {
        scrollToElement(wrappersWithErrors[0], {
          offset: navbarOffset,
        });
      }
    }, 1);
  }, [currentEvent, formData.eventResponsesByEventId, navbarOffset]);

  const onNextClick = useCallback(() => {
    if (isFormComplete) {
      setCurrentEventIndex((prev) => prev + 1);
      setHasNextBeenClicked(false);
      setGuestsFormsToValidate([]);
    } else {
      updateGuestIdsToValidate();
    }
  }, [isFormComplete, updateGuestIdsToValidate]);

  const onBackClick = useCallback(() => {
    setCurrentEventIndex((prev) => prev - 1);
    setHasNextBeenClicked(false);
    setGuestsFormsToValidate([]);
  }, []);

  const submitRsvp = useCallback(() => {
    if (!isFormComplete) {
      updateGuestIdsToValidate();

      return null;
    }

    // Prevent RSVP submission if any email is invalid
    if (
      formData.emails.some((email) => email.value && !isEmailValid(email.value))
    ) {
      return null;
    }
    // Wedding websites has to loop over each event and guest to create the RSVP
    // structure, as well as include guest name information
    const eventResponses: {
      eventId: string;
      eventResponseData: {
        answers: { answer: string; questionId: string }[];
        guestId: string;
        responseType: string;
      }[];
    }[] = [];

    for (const [eventId, guestResponsesForEvent] of Object.entries(
      formData.eventResponsesByEventId,
    )) {
      // Flatten guest responses object to list
      const guestResponses: {
        answers: { answer: string; questionId: string }[];
        guestId: string;
        responseType: string;
      }[] = [];

      for (const [guestId, guestResponse] of Object.entries(
        guestResponsesForEvent,
      )) {
        // Flatten answers object to list
        const answers: { answer: string; questionId: string }[] = [];

        for (const [questionId, answer] of Object.entries(
          guestResponse.answers,
        )) {
          answers.push({
            answer,
            questionId,
          });
        }
        guestResponses.push({
          answers,
          guestId,
          responseType: guestResponse.responseType,
        });
      }
      eventResponses.push({
        eventId,
        eventResponseData: guestResponses,
      });
    }

    // Flatten guest names as well
    const updatedGuestNames: ({ guestId: string } & UpdatedGuestName)[] = [];

    for (const [guestId, newGuestName] of Object.entries(
      formData.updatedGuestNamesByGuestId,
    )) {
      updatedGuestNames.push({
        guestId,
        ...newGuestName,
      });
    }
    const requestData = {
      addressbookContactId: formData.contactId,
      emails: formData.emails
        .map((email) => email.value)
        .filter((email) => Boolean(email)),
      eventResponses,
      updatedGuestNames,
    };

    setIsSubmitting(true);
    setRsvpSubmitError(null);

    API.post(
      "guest-management/rsvp-household",
      getGuestAuthSettings(),
      requestData,
    )
      .then(() => {
        localStorage.setItem(
          RSVP_LOCAL_STORAGE_KEY,
          JSON.stringify({
            contact,
            currentEventIndex,
            events,
            formData,
            guests,
            showSummary: true,
          }),
        );

        setShowSummary(true);

        scrollToElement(wrapper.current, {
          offset: navbarOffset,
        });
      })
      .catch((error) => {
        console.error("RSVP submission error:", error);
        setRsvpSubmitError(error);
      })
      .finally(() => setIsSubmitting(false));
  }, [
    contact,
    currentEventIndex,
    events,
    formData,
    guests,
    isFormComplete,
    navbarOffset,
    updateGuestIdsToValidate,
  ]);

  const onChangeResponse = () => {
    setCurrentEventIndex(0);
    setShowSummary(false);
    setRsvpSubmitError(null);
    setHasNextBeenClicked(false);
    setGuestsFormsToValidate([]);
  };

  useEffect(() => {
    scrollToElement(wrapper.current, {
      offset: navbarOffset,
    });
  }, [currentEventIndex, navbarOffset]);

  return (
    <div
      css={[rsvpStyles.wrapper, themeTypographyResetStyles]}
      data-cy="rsvpForm"
      ref={wrapper}
    >
      <ConfirmationModal
        onCloseClick={onCancelRsvp}
        onSubmit={() => setHasAcknowledgedExistingRsvp(true)}
        open={hasExistingRsvps && !showSummary && !hasAcknowledgedExistingRsvp}
        showCancel
        submitButtonText="Open RSVP Form"
        alertPrimaryText="An RSVP was already submitted."
      >
        If you need to change your answers, contact the host or submit this form
        again.
      </ConfirmationModal>
      {showSummary ? (
        <div>
          <div css={rsvpStyles.rsvpForAnotherGuestWrapper}>
            <TextLink
              icon="arrow-left"
              onClick={onCancelRsvp}
              text="RSVP for another Guest"
              type={TextLink.textLinkTypes.primary}
            />
          </div>
          <WeddingEventsGuestsSummary
            contact={contact}
            events={events}
            guests={guests}
            guestsResponses={formData.eventResponsesByEventId}
            isMobile={isMobile}
            updatedGuestNames={formData.updatedGuestNamesByGuestId}
          />
          <div css={rsvpStyles.buttonWrapper}>
            <Button
              expand
              onClick={onChangeResponse}
              size="large"
              text="Change Response"
              type="primary"
            />
          </div>
        </div>
      ) : hasAcknowledgedExistingRsvp ? (
        <>
          <EventDetails
            calendarUrls={currentEvent.calendarUrls}
            event={currentEvent}
            index={currentEventIndex}
            isMobile={isMobile}
            key={currentEvent.id} // Adding key to prevent iOS Chrome error when event changes
            totalEvents={totalEvents}
          />

          <div css={rsvpStyles.formWrapper}>
            {guests
              .filter((guest) => {
                return currentEvent.guests.includes(guest.id);
              })
              .map((guest) => (
                <WeddingEventGuest
                  canEditName={canEditName}
                  contactName={contact.name}
                  guest={guest}
                  guestResponse={
                    formData.eventResponsesByEventId[currentEvent.id][guest.id]
                  }
                  key={`${guest.id}`}
                  questions={currentEvent.questions}
                  updatedGuestName={
                    formData.updatedGuestNamesByGuestId[guest.id]
                  }
                  updateGuestName={updateGuestName}
                  updateGuestResponses={updateGuestResponses}
                  validateErrors={
                    hasNextBeenClicked &&
                    guestsFormsToValidate.includes(guest.id)
                  }
                />
              ))}
          </div>

          {showSubmitButton && (
            <div css={rsvpStyles.emailMargins}>
              <EventSummaryEmail
                emails={formData.emails}
                eventCount={totalEvents}
                isSubmitting={isSubmitting}
                updateEmails={updateEmails}
              />
            </div>
          )}

          <div css={rsvpStyles.buttonsWrapper}>
            {showBackButton && (
              <Button
                dataAttrs={[["cy", selectors.rsvp_backButton]]}
                disabled={isSubmitting}
                expand
                onClick={onBackClick}
                size="large"
                text="Back"
                type="secondary"
              />
            )}

            {showNextButton && (
              <Button
                dataAttrs={[["cy", selectors.rsvp_nextButton]]}
                expand
                onClick={onNextClick}
                size="large"
                text="Next"
                type="primary"
              />
            )}
            <div css={rsvpStyles.submitButtonWrapper}>
              {showSubmitButton && (
                <Button
                  dataAttrs={[["cy", selectors.rsvp_submitButton]]}
                  disabled={isSubmitting || rsvpSubmitError}
                  expand
                  onClick={submitRsvp}
                  size="large"
                  text="Submit"
                  type="primary"
                />
              )}
              {rsvpSubmitError && (
                <div css={rsvpStyles.errorLabel}>
                  {getErrorMessage(rsvpSubmitError)}
                </div>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default WeddingRSVPForm;
